import { useEffect, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { createPortal } from 'react-dom';
import { Modal } from '@/components/atoms/Modal';
import { apiFetch, useAuthStore } from '@/stores/authStore';

interface RestartStatus {
  deployment: string; bootId: string; available: boolean; reason: string | null; cooldownMs: number;
  operation: { requestId: string; status: 'accepted' | 'replacement_observed'; acceptedAt: string; observedAt: string | null } | null;
}
const endpoint = '/api/admin/relay-restart';

export function RelayRestartControl() {
  const user = useAuthStore(s => s.user);
  if (user?.role !== 'admin') return null;
  return <RelayRestartButton key={`${user.id}:${user.orgId}`} scope={`${user.id}:${user.orgId}`} />;
}

function RelayRestartButton({ scope }: { scope: string }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<RestartStatus | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [notified, setNotified] = useState(false);
  const [pending, setPending] = useState(false);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [readGeneration, setReadGeneration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const closeButton = useRef<HTMLButtonElement>(null);
  const deploymentBox = useRef<HTMLInputElement>(null);
  const storageKey = `g1-relay-restart:${location.host}:${scope}`;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  useEffect(() => {
    if (!open) return;
    closeButton.current?.focus();
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const started = Date.now();
    async function read() {
      try {
        const response = await apiFetch(`${endpoint}${attemptId ? `?requestId=${encodeURIComponent(attemptId)}` : ''}`, { signal: controller.signal });
        const value = await response.json() as RestartStatus & { error?: string };
        if (controller.signal.aborted) return;
        if (!response.ok) throw new Error(value.error ?? 'Restart status could not be checked.');
        setStatus(value);
        if (!attemptId) setError(null);
        if (value.operation?.requestId === attemptId && value.operation.status === 'replacement_observed') {
          setPending(false);
          setError(null);
          return;
        }
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(e instanceof Error ? e.message : 'Restart status could not be checked.');
      }
      // Readback only. Never retry POST, even after a network failure or reboot.
      if (attemptId && Date.now() - started < 120_000) timer = setTimeout(() => void read(), 5_000);
    }
    void read();
    return () => { controller.abort(); if (timer) clearTimeout(timer); };
  }, [open, attemptId, readGeneration]);

  const show = () => {
    setStatus(null); setError(null); setConfirmation(''); setCopyStatus(''); setNotified(false);
    try { const saved = sessionStorage.getItem(storageKey); setAttemptId(saved); setPending(!!saved); }
    catch { setError('This browser cannot retain a restart receipt. Use another browser session.'); }
    setOpen(true);
  };
  const copyDeployment = async () => {
    if (!status?.deployment) return;
    try {
      await navigator.clipboard.writeText(status.deployment);
      if (mounted.current) setCopyStatus('Copied. Paste it in the confirmation box below.');
    } catch {
      deploymentBox.current?.focus(); deploymentBox.current?.select();
      if (mounted.current) setCopyStatus('Select and copy the deployment name above.');
    }
  };
  const submit = async () => {
    if (inFlight.current || attemptId || !status?.available || !notified
      || confirmation !== status.deployment || status.cooldownMs > 0) return;
    const requestId = crypto.randomUUID();
    try { sessionStorage.setItem(storageKey, requestId); }
    catch { setError('Could not retain the restart receipt. Nothing was sent.'); return; }
    inFlight.current = true;
    setPending(true); setAttemptId(requestId); setError(null);
    try {
      // apiFetch retries on401. This irreversible control intentionally sends
      // once; the immediately preceding status read already refreshed auth.
      const token = useAuthStore.getState().token;
      const response = await fetch(endpoint, { method: 'POST',
        headers: { Authorization: `Bearer ${token ?? ''}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestId, bootId: status.bootId, confirmation, tradersNotified: true }),
      });
      const value = await response.json() as { error?: string; code?: string };
      if (!response.ok && mounted.current) {
        setError(value.error ?? 'The request was refused. Check status before trying again.');
        // Only a definite pre-dispatch refusal clears the retained request.
        // A503/network/parse error can follow a committed claim and stays held.
        if (response.status === 401 || response.status === 413
          || ([400,403,409].includes(response.status) && value.code
          && ['forbidden','invalid_request','stale_boot','busy','cooldown'].includes(value.code))) {
          sessionStorage.removeItem(storageKey); setAttemptId(null); setPending(false);
          setNotified(false); setConfirmation('');
        }
      }
    } catch {
      if (mounted.current) setError('The reply was interrupted. Checking the saved request; do not submit again.');
    } finally { inFlight.current = false; }
  };
  const completed = attemptId && status?.operation?.requestId === attemptId
    && status.operation.status === 'replacement_observed';

  return <>
    <button type="button" onClick={show} className="text-muted-foreground hover:bg-accent flex shrink-0 items-center gap-1 rounded px-1.5 py-1"
      title="Administrator relay restart" aria-label="Restart relay"><RotateCcw className="h-3 w-3" />relay</button>
    {open && createPortal(<Modal isOpen={open} onClose={() => setOpen(false)} title="Restart this relay?" size="lg" closeOnBackdrop={false}
      footer={<div className="flex justify-end gap-3"><button ref={closeButton} type="button" onClick={() => setOpen(false)} className="rounded border px-3 py-2">Close</button>
        {!attemptId && <button type="button" onClick={() => void submit()}
          disabled={!status?.available || status.cooldownMs > 0 || !notified || confirmation !== status.deployment || pending}
          className="rounded bg-red-700 px-3 py-2 font-semibold text-white disabled:opacity-40">Restart relay</button>}
        {completed && <button type="button" className="rounded border px-3 py-2" onClick={() => {
          sessionStorage.removeItem(storageKey); setAttemptId(null); setPending(false); setOpen(false);
        }}>Acknowledge result</button>}
        {attemptId && !completed && <button type="button" className="rounded border px-3 py-2" onClick={() => setReadGeneration(v=>v+1)}>Check restart status</button>}</div>}>
      <div className="space-y-4 text-sm">
        <p className="rounded border border-red-500/50 bg-red-500/10 p-3 text-base font-semibold">Inform all traders on this deployment before continuing.</p>
        <p>This interrupts every organization using this relay, including trading requests, account updates and in-relay automation. Accepted orders may need reconciliation after reconnecting. Bots follow their usual restart recovery.</p>
        <p>It restarts the current relay with its installed code. It does not restart Hydra. If the relay is fully unresponsive, this control cannot reach it.</p>
        {attemptId && <p>Deployment: <strong>{status?.deployment || location.host}</strong></p>}
        {!status && <p role="status">Checking current relay…</p>}
        {status && !status.available && <p role="status">{status.reason}</p>}
        {!!status?.cooldownMs && !attemptId && <p role="status">A restart was recently accepted. Wait at least {Math.ceil(status.cooldownMs / 1000)} seconds.</p>}
        {!attemptId && <>
          <label className="flex items-start gap-2"><input type="checkbox" checked={notified} onChange={e => setNotified(e.target.checked)} />I have informed all traders on this deployment.</label>
          <div>
            <label className="block" htmlFor="relay-restart-deployment">Deployment name</label>
            <div className="mt-1 flex gap-2">
              <input id="relay-restart-deployment" ref={deploymentBox} readOnly value={status?.deployment ?? ''}
                onFocus={e => e.currentTarget.select()} className="min-w-0 flex-1 rounded border bg-background px-2 py-2 font-mono" />
              <button type="button" disabled={!status?.deployment} onClick={() => void copyDeployment()}
                className="w-20 shrink-0 rounded border px-3 py-2 disabled:opacity-40" aria-label="Copy deployment name"
                title={copyStatus || undefined}>{copyStatus.startsWith('Copied.') ? 'Copied' : 'Copy'}</button>
            </div>
            <p role="status" className="sr-only">{copyStatus}</p>
          </div>
          <label className="block">Type or paste the deployment name to confirm<input className="mt-1 block w-full rounded border bg-background px-2 py-2" value={confirmation} onChange={e => setConfirmation(e.target.value)} autoComplete="off" /></label>
        </>}
        {attemptId && <p role="status">{completed
          ? 'A replacement relay is responding. Check feeds, account updates and trading before telling traders it has recovered.'
          : 'Request retained. Checking whether a replacement relay returns. An interrupted reply does not mean the restart failed; do not repeat it.'}</p>}
        {error && <p role="alert" className="text-red-500">{error}</p>}
      </div>
    </Modal>, document.body)}
  </>;
}
